"use client";

import { useCallback, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { uploadLiveaboardImageToMinio } from "@/app/libs/liveaboardMinioUpload";

type Props = {
  liveaboardId: string;
  value: string;
  onChange: (url: string) => void;
  label?: string;
  hint?: string;
};

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";

export default function LiveaboardMinioUpload({
  liveaboardId,
  value,
  onChange,
  label = "Photo",
  hint,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!ACCEPT.split(",").some((t) => file.type === t.trim())) {
        toast.error("Use PNG, JPG or WebP.");
        return;
      }
      setUploading(true);
      try {
        const url = await uploadLiveaboardImageToMinio(liveaboardId, file);
        onChange(url);
        toast.success("Photo uploaded.");
      } catch {
        toast.error("Upload failed.");
      } finally {
        setUploading(false);
      }
    },
    [liveaboardId, onChange]
  );

  const onFiles = (files: FileList | File[]) => {
    const file = Array.from(files)[0];
    if (file) void uploadFile(file);
  };

  const dropHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types?.includes("Files")) setIsDragging(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const next = e.relatedTarget as Node | null;
      if (!next || !e.currentTarget.contains(next)) setIsDragging(false);
    },
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!uploading) onFiles(e.dataTransfer?.files ?? []);
    },
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs font-medium text-neutral-600">
          {label}
        </label>
      )}
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        disabled={uploading}
        onChange={(e) => {
          onFiles(e.target.files ?? []);
          e.target.value = "";
        }}
      />

      {value ? (
        <div
          {...dropHandlers}
          onClick={() => !uploading && fileRef.current?.click()}
          className={`relative rounded-xl overflow-hidden border-2 cursor-pointer transition ${
            isDragging
              ? "border-cyan-600 ring-2 ring-cyan-200"
              : "border-neutral-200 hover:border-cyan-500"
          } ${uploading ? "opacity-60 pointer-events-none" : ""}`}
        >
          <div className="aspect-[4/3] max-h-56 bg-neutral-100">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={value}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div
            className={`absolute inset-0 flex items-center justify-center bg-black/40 text-white text-sm font-medium transition ${
              isDragging ? "opacity-100" : "opacity-0 hover:opacity-100"
            }`}
          >
            {uploading ? "Uploading…" : "Drag & drop or click to replace"}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            className="absolute top-2 right-2 rounded-full bg-black/60 text-white text-xs px-2.5 py-1 hover:bg-rose-600"
          >
            Remove
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") fileRef.current?.click();
          }}
          {...dropHandlers}
          onClick={() => !uploading && fileRef.current?.click()}
          className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            uploading
              ? "opacity-50 cursor-not-allowed border-neutral-200 bg-neutral-50"
              : isDragging
                ? "border-cyan-600 bg-cyan-50"
                : "border-neutral-300 bg-neutral-50/50 hover:border-cyan-500 hover:bg-cyan-50/40"
          }`}
        >
          <p className="text-sm font-medium text-neutral-700">
            {uploading ? "Uploading…" : "Drag & drop or click to upload"}
          </p>
          <p className="text-xs text-neutral-500 mt-1">PNG, JPG or WebP</p>
        </div>
      )}
    </div>
  );
}
